import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Paper,
  Popover,
  Radio,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";

import {
  getSopAdditionalPermissionsByApplicationType,
  getDomainList,
  getSopRolesByApplicationTypeAndRoleType
} from "../../../../services/S&OP/S&OPServices";
import { putEditElmUser } from "../../../../services/S&OP/BottlerElmServices";
import { APPLICATION_TYPE_MAP } from "../../../../constants/userAccess";
import { inviteBottlerFormStyles as styles } from "./constants/inviteBottlerFormStyles";
import {
  TOAST_UPDATE_ERROR,
  TOAST_UPDATE_SUCCESS,
} from "./constants/bottlerElmAccessText";

const APPLICATION_TYPE =
  APPLICATION_TYPE_MAP["S&OP - Sales And Operations Planning"] ||
  "SALES_AND_OPERATIONAL_PLANNING";
const ROLE_TYPE_BOTTLER = "BOTTLER";
const APPLICATION_LABEL = "Sales and Operations Planning";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WHITELISTED_DOMAIN_STATUSES = new Set(["ACTIVE", "ONBOARDED"]);

const isDomainWhitelisted = (emailAddress, domainList) => {
  const emailParts = emailAddress.trim().split("@");
  if (emailParts.length !== 2) return true;
  const emailDomain = emailParts[1].toLowerCase();
  return domainList.some((domain) =>
    String(domain?.domainName || "").toLowerCase() === emailDomain &&
    WHITELISTED_DOMAIN_STATUSES.has(String(domain?.status || "").toUpperCase())
  );
};

const normalizeRolesAndRegions = (responseData) => {
  const roles = Array.isArray(responseData)
    ? responseData
    : Array.isArray(responseData?.roles)
      ? responseData.roles
      : [];
  return roles.map((role) => ({
    ...role,
    regions: Array.isArray(role.regions) ? role.regions : [],
  }));
};

const normalizeToken = (value) => String(value || "")
  .trim()
  .toLocaleLowerCase()
  .replace(/[^a-z0-9]+/g, "");

const matchesScopeValue = (configuredValue, savedValue) => {
  const configuredToken = normalizeToken(configuredValue);
  const savedToken = normalizeToken(savedValue);
  if (!configuredToken || !savedToken) return false;
  if (configuredToken === savedToken) return true;

  // The list API can return a short code/name while access config returns a
  // compound label (for example "US" vs "US - United States").
  return configuredToken.startsWith(savedToken) ||
    configuredToken.endsWith(savedToken) ||
    savedToken.startsWith(configuredToken) ||
    savedToken.endsWith(configuredToken);
};

// Region codes must match exactly: fuzzy prefix/suffix matching wrongly pairs
// codes like "EOU" with "EMEOU" or "AOU" with "LAOU".
const matchesRegionValue = (configuredValue, savedValue) => {
  const configuredToken = normalizeToken(configuredValue);
  const savedToken = normalizeToken(savedValue);
  return Boolean(configuredToken) && configuredToken === savedToken;
};

const flattenValues = (value) => {
  if (Array.isArray(value)) return value.flatMap(flattenValues);
  if (value && typeof value === "object") return Object.values(value).flatMap(flattenValues);
  if (value === undefined || value === null || value === "") return [];
  return [String(value)];
};

const arraysEqualUnordered = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

const attributeSelectionsEqual = (a = {}, b = {}) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => arraysEqualUnordered(a[key] || [], b[key] || []));
};

const firstPopulated = (...values) => values.find((value) => {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
});

const getScopeValues = (source, region) => {
  if (source && typeof source === "object" && !Array.isArray(source)) {
    const matchingRegionKey = Object.keys(source).find(
      (key) => normalizeToken(key) === normalizeToken(region?.regionCode) ||
        normalizeToken(key) === normalizeToken(region?.regionName)
    );
    if (matchingRegionKey) return flattenValues(source[matchingRegionKey]);
  }
  return flattenValues(source);
};

const resolveInitialEditState = (editData, roles, attributes) => {
  const roleCandidates = [
    editData?.roleId,
    editData?.accessLevel,
    editData?.role,
    editData?.roleName,
  ].filter(Boolean);
  const selectedRole = roles.find((role) =>
    roleCandidates.some((candidate) =>
      matchesScopeValue(role.roleId, candidate) ||
      matchesScopeValue(role.roleName, candidate)
    )
  ) || null;

  const regionCandidates = flattenValues(
    firstPopulated(editData?.regions, editData?.region)
  );
  const selectedRegion = (selectedRole?.regions || []).find((region) =>
    regionCandidates.some((candidate) =>
      matchesRegionValue(region.regionCode, candidate) ||
      matchesRegionValue(region.regionName, candidate)
    )
  ) || null;
  const selectedRegionCode = selectedRegion?.regionCode || "";

  const countryValues = getScopeValues(
    firstPopulated(editData?.countries, editData?.countryList, editData?.country),
    selectedRegion
  );
  const bottlerValues = getScopeValues(
    firstPopulated(editData?.bottlers, editData?.bottlerGroups, editData?.bottler),
    selectedRegion
  );
  const selectedCountryEntries = (selectedRegion?.countries || [])
    .filter((country) => countryValues.some((savedCountry) =>
      matchesScopeValue(country.countryName, savedCountry) ||
      matchesScopeValue(country.countryCode, savedCountry)
    ));
  const selectedCountries = selectedCountryEntries.map((country) => country.countryCode);
  // Only scan bottlers within the matched countries above; otherwise a bottler match
  // for a country that failed the country match would slip into selectedBottlers,
  // get silently stripped by the bottlerOptions cleanup effect on mount, and make
  // hasChanges true immediately without any user edit.
  const selectedBottlers = selectedCountryEntries.flatMap((country) =>
    (country.bottlers || [])
      .filter((bottler) => bottlerValues.some((savedBottler) =>
        matchesScopeValue(bottler.bottlerName, savedBottler) ||
        matchesScopeValue(bottler.bottlerId, savedBottler)
      ))
      .map((bottler) => `${country.countryCode}::${bottler.bottlerId}`)
  );

  const rawPermissions = firstPopulated(
    editData?.additionalPermissions,
    editData?.additionalPermission
  ) || {};
  const selectedAttributes = Object.entries(rawPermissions).reduce((result, [key, values]) => {
    const attribute = attributes.find((item) =>
      normalizeToken(key) === normalizeToken(item.attributeId) ||
      normalizeToken(key) === normalizeToken(item.attributeName)
    );
    if (!attribute) return result;
    const valueTokens = new Set(flattenValues(values).map(normalizeToken));
    const selectedValueIds = (attribute.values || [])
      .filter((value) =>
        valueTokens.has(normalizeToken(value.id)) || valueTokens.has(normalizeToken(value.name))
      )
      .map((value) => value.id);
    if (selectedValueIds.length) result[attribute.attributeId] = selectedValueIds;
    return result;
  }, {});

  return {
    inviteeEmail: String(
      editData?.userEmail || editData?.requestedUserEmail || editData?.requestedFor || ""
    ),
    selectedRoleId: selectedRole?.roleId || "",
    selectedRegions: selectedRegionCode ? [selectedRegionCode] : [],
    selectedCountries,
    selectedBottlers,
    selectedAttributes,
    customize: Object.keys(rawPermissions).length > 0,
  };
};

const EditBottlerForm = ({
  requestedByEmail = "",
  applicationType = APPLICATION_TYPE,
  editData = {},
  onCancel,
  onUpdated,
}) => {
  const [roles, setRoles] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [domainList, setDomainList] = useState([]);
  const [domainListLoading, setDomainListLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedBottlers, setSelectedBottlers] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [attributeId, setAttributeId] = useState("");
  const [attributeValueIds, setAttributeValueIds] = useState([]);
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [configurationError, setConfigurationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [customize, setCustomize] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [attributePopover, setAttributePopover] = useState({ anchorEl: null, attributeId: "" });
  const [initialFormState, setInitialFormState] = useState(null);
  const attributePopoverCloseTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const backendApplicationType = useMemo(
    () => APPLICATION_TYPE_MAP[applicationType] || applicationType || APPLICATION_TYPE,
    [applicationType]
  );
  const selectedRole = useMemo(
    () => roles.find((role) => role.roleId === selectedRoleId) || null,
    [roles, selectedRoleId]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setConfigurationError("");
    Promise.all([
      getSopRolesByApplicationTypeAndRoleType(
        backendApplicationType,
        ROLE_TYPE_BOTTLER
      ),
      getSopAdditionalPermissionsByApplicationType(backendApplicationType),
    ])
      .then(([accessConfigResponse, attributeResponse]) => {
        if (!active) return;
        const roleList = normalizeRolesAndRegions(accessConfigResponse);
        const attributeList = Array.isArray(attributeResponse) ? attributeResponse : [];
        const initialState = resolveInitialEditState(editData, roleList, attributeList);
        const missingFields = [
          !initialState.inviteeEmail && "email",
          !initialState.selectedRoleId && "role",
          !initialState.selectedRegions.length && "region",
          !initialState.selectedCountries.length && "country",
          !initialState.selectedBottlers.length && "bottler",
        ].filter(Boolean);

        if (missingFields.length) {
          setConfigurationError(
            `Unable to autofill complete bottler details. Missing or unmatched: ${missingFields.join(", ")}.`
          );
          return;
        }

        setRoles(roleList);
        setAttributes(attributeList);
        setSelectedRoleId(initialState.selectedRoleId);
        setSelectedRegions(initialState.selectedRegions);
        setSelectedCountries(initialState.selectedCountries);
        setSelectedBottlers(initialState.selectedBottlers);
        setSelectedAttributes(initialState.selectedAttributes);
        setCustomize(initialState.customize);
        setInviteeEmail(initialState.inviteeEmail);
        setInitialFormState(initialState);
      })
      .catch(() => {
        if (!active) return;
        setConfigurationError("Unable to load access configuration.");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [backendApplicationType, editData]);

  useEffect(() => {
    let active = true;
    setDomainListLoading(true);
    getDomainList()
      .then((domains) => {
        if (active) setDomainList(Array.isArray(domains) ? domains : []);
      })
      .catch(() => {
        if (!active) return;
        setDomainList([]);
        setMessage({ severity: "error", text: "Unable to load whitelisted domains." });
      })
      .finally(() => {
        if (active) setDomainListLoading(false);
      });
    return () => { active = false; };
  }, [backendApplicationType]);

  const countryOptions = useMemo(() =>
    (selectedRole?.regions || []).flatMap((region) =>
      selectedRegions.includes(region.regionCode)
        ? (region.countries || []).map((country) => ({
            key: country.countryCode,
            regionCode: region.regionCode,
            countryCode: country.countryCode,
            countryName: country.countryName,
            label: country.countryName,
            groupKey: region.regionCode,
          }))
        : []
    ), [selectedRole, selectedRegions]);

  const bottlerOptions = useMemo(() =>
    (selectedRole?.regions || []).flatMap((region) =>
      selectedRegions.includes(region.regionCode)
        ? (region.countries || []).flatMap((country) =>
            selectedCountries.includes(country.countryCode)
              ? (country.bottlers || []).map((bottler) => ({
                  key: `${country.countryCode}::${bottler.bottlerId}`,
                  regionCode: region.regionCode,
                  countryCode: country.countryCode,
                  countryName: country.countryName,
                  bottlerId: bottler.bottlerId,
                  label: bottler.bottlerName,
                  displayLabel: `${bottler.bottlerId}_${bottler.bottlerName}`,
                  groupKey: country.countryCode,
                  groupLabel: country.countryName,
                }))
              : []
          )
        : []
    ), [selectedRole, selectedRegions, selectedCountries]);

  useEffect(() => {
    const valid = new Set(countryOptions.map((item) => item.key));
    setSelectedCountries((current) => current.filter((key) => valid.has(key)));
  }, [countryOptions]);

  useEffect(() => {
    const valid = new Set(bottlerOptions.map((item) => item.key));
    setSelectedBottlers((current) => current.filter((key) => valid.has(key)));
  }, [bottlerOptions]);

  const lockedRegionCode = initialFormState?.selectedRegions?.[0] || selectedRegions[0] || "";
  const lockedRegionItems = lockedRegionCode
    ? [{
        key: lockedRegionCode,
        label: lockedRegionCode,
        disabled: true,
        noDataText: "Cannot be changed",
      }]
    : [];

  const toggle = (setter, value) => setter((current) =>
    current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
  );

  const resetDependentSelections = () => {
    setSelectedCountries([]);
    setSelectedBottlers([]);
    setSelectedAttributes({});
    setAttributeId("");
    setAttributeValueIds([]);
  };

  const handleRoleChange = (event) => {
    setSelectedRoleId(event.target.value);
    setCustomize(false);
    resetDependentSelections();
  };

  const handleReset = () => {
    if (!initialFormState) return;
    setSelectedRoleId(initialFormState.selectedRoleId);
    setSelectedRegions(initialFormState.selectedRegions);
    setSelectedCountries(initialFormState.selectedCountries);
    setSelectedBottlers(initialFormState.selectedBottlers);
    setSelectedAttributes(initialFormState.selectedAttributes);
    setAttributeId("");
    setAttributeValueIds([]);
    setCustomize(initialFormState.customize);
    setInviteeEmail(initialFormState.inviteeEmail);
    setShowPreview(false);
  };


  const isInviteeEmailValid = EMAIL_REGEX.test(inviteeEmail.trim());
  const isInviteeDomainWhitelisted = isInviteeEmailValid && isDomainWhitelisted(inviteeEmail, domainList);
  const showDomainError = isInviteeEmailValid && !domainListLoading && !isInviteeDomainWhitelisted;
  const emailDomainStatus = !isInviteeEmailValid
    ? "idle"
    : domainListLoading
      ? "checking"
      : isInviteeDomainWhitelisted
        ? "verified"
        : "rejected";
  const emailDomainHelperText = emailDomainStatus === "checking"
    ? "Checking domain status..."
    : emailDomainStatus === "verified"
      ? "This domain is active (whitelisted)."
      : emailDomainStatus === "rejected"
        ? "Domain not whitelisted"
        : "";
  const emailDomainStatusLabel = emailDomainStatus === "checking"
    ? "Checking email domain"
    : emailDomainStatus === "verified"
      ? "Domain whitelisted and active"
      : "Domain not whitelisted";
  const isRequestDetailsComplete = Boolean(applicationType) &&
    !domainListLoading &&
    isInviteeEmailValid &&
    isInviteeDomainWhitelisted;
  const isRoleComplete = Boolean(selectedRole);
  const isRegionComplete = (selectedRole?.regions || [])
    .some((region) => selectedRegions.includes(region.regionCode));
  const isCountryComplete = countryOptions
    .some((country) => selectedCountries.includes(country.key));
  // Every selected country must have at least one bottler picked, not just one bottler overall.
  const isBottlerComplete = selectedCountries.length > 0 &&
    selectedCountries.every((countryKey) => bottlerOptions.some(
      (bottler) => bottler.countryCode === countryKey && selectedBottlers.includes(bottler.key)
    ));
  const isScopeComplete = isRegionComplete && isCountryComplete && isBottlerComplete;
  const isFormComplete = isRequestDetailsComplete && isRoleComplete && isScopeComplete;

  const hasChanges = useMemo(() => {
    if (!initialFormState) return false;
    return (
      selectedRoleId !== initialFormState.selectedRoleId ||
      customize !== initialFormState.customize ||
      !arraysEqualUnordered(selectedRegions, initialFormState.selectedRegions) ||
      !arraysEqualUnordered(selectedCountries, initialFormState.selectedCountries) ||
      !arraysEqualUnordered(selectedBottlers, initialFormState.selectedBottlers) ||
      !attributeSelectionsEqual(selectedAttributes, initialFormState.selectedAttributes)
    );
  }, [initialFormState, selectedRoleId, customize, selectedRegions, selectedCountries, selectedBottlers, selectedAttributes]);

  const addAttribute = () => {
    if (!attributeId) return;
    const nextValueIds = Array.from(new Set(attributeValueIds));
    setSelectedAttributes((current) => {
      const next = { ...current };
      if (nextValueIds.length) {
        next[attributeId] = nextValueIds;
      } else {
        delete next[attributeId];
      }
      return next;
    });
  };

  const removeAttribute = (id, valueId) => {
    setSelectedAttributes((current) => {
      const next = { ...current, [id]: (current[id] || []).filter((value) => value !== valueId) };
      if (!next[id].length) delete next[id];
      return next;
    });
    if (attributeId === id) {
      setAttributeValueIds((current) => current.filter((value) => value !== valueId));
    }
  };

  const clearAttributePopoverCloseTimer = () => {
    if (attributePopoverCloseTimerRef.current) {
      clearTimeout(attributePopoverCloseTimerRef.current);
      attributePopoverCloseTimerRef.current = null;
    }
  };

  const closeAttributePopover = () => {
    clearAttributePopoverCloseTimer();
    setAttributePopover({ anchorEl: null, attributeId: "" });
  };

  const handleCustomizeChange = (event) => {
    const checked = event.target.checked;
    setCustomize(checked);
    if (!checked) closeAttributePopover();
  };

  const openAttributePopover = (event, id) => {
    clearAttributePopoverCloseTimer();
    setAttributePopover({ anchorEl: event.currentTarget, attributeId: id });
  };

  const scheduleAttributePopoverClose = () => {
    clearAttributePopoverCloseTimer();
    attributePopoverCloseTimerRef.current = setTimeout(() => {
      setAttributePopover({ anchorEl: null, attributeId: "" });
    }, 220);
  };

  useEffect(() => () => clearAttributePopoverCloseTimer(), []);

  const buildPayload = () => {
    // Backend expects a single "region" string and a flat "countries" array, not grouped by region.
    const countries = countryOptions
      .filter((item) => selectedCountries.includes(item.key))
      .map((item) => item.label);
    const bottlers = {};
    bottlerOptions.filter((item) => selectedBottlers.includes(item.key)).forEach((item) => {
      bottlers[item.countryName] = [...(bottlers[item.countryName] || []), item.label];
    });
    const additionalPermissionsPayload = Object.entries(selectedAttributes).reduce((acc, [id, valueIds]) => {
      const config = attributes.find((item) => item.attributeId === id);
      const valueNames = valueIds
        .map((valueId) => config?.values.find((item) => item.id === valueId)?.name || valueId)
        .filter(Boolean);
      if (valueNames.length) {
        acc[config?.attributeName || id] = valueNames;
      }
      return acc;
    }, {});

    const payload = {
      role: selectedRoleId,
      region: lockedRegionCode || selectedRegions[0] || "",
      countries,
      bottlers,
      email: requestedByEmail,
      requestedFor: inviteeEmail.trim(),
      // There is no name-entry field on edit; reuse the name already on record.
      requestedForUserName: editData?.userName || editData?.requestedForUserName || "",
      applicationType: backendApplicationType,
    };

    const rawPermissions = firstPopulated(
      editData?.additionalPermissions,
      editData?.additionalPermission
    );
    const hasPermissionField = editData && (
      Object.prototype.hasOwnProperty.call(editData, "additionalPermissions") ||
      Object.prototype.hasOwnProperty.call(editData, "additionalPermission")
    );
    if (hasPermissionField || customize || Object.keys(additionalPermissionsPayload).length > 0) {
      const unmappedPermissions = customize && rawPermissions && typeof rawPermissions === "object"
        ? Object.entries(rawPermissions).reduce((result, [key, values]) => {
            const isMapped = attributes.some((item) =>
              normalizeToken(key) === normalizeToken(item.attributeId) ||
              normalizeToken(key) === normalizeToken(item.attributeName)
            );
            if (!isMapped) result[key] = flattenValues(values);
            return result;
          }, {})
        : {};
      payload.additionalPermissions = customize
        ? { ...unmappedPermissions, ...additionalPermissionsPayload }
        : {};
    }
    return payload;
  };

  const validate = () => {
    if (!inviteeEmail.trim()) return "Please enter an email address.";
    if (!EMAIL_REGEX.test(inviteeEmail.trim())) return "Please enter a valid email address.";
    if (domainListLoading) return "Please wait while the email domain is validated.";
    if (!isInviteeDomainWhitelisted) return "Email domain is not whitelisted.";
    if (!isRoleComplete) return "Please select a role.";
    if (!isRegionComplete) return "Please select a region.";
    if (!isCountryComplete) return "Please select at least one country.";
    if (!isBottlerComplete) return "Please select at least one bottler for each selected country.";
    return "";
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setMessage({ severity: "error", text: validationError });
      return;
    }
    setSubmitting(true);
    try {
      const response = await putEditElmUser(buildPayload());
      toast.success(response?.message || TOAST_UPDATE_SUCCESS, { autoClose: 2000 });
      if (onUpdated) await onUpdated(response);
    } catch (error) {
      const responseData = error?.response?.data;
      const errorMessage = responseData?.errorMessage || TOAST_UPDATE_ERROR;
      toast.error(errorMessage, { autoClose: 2000 });
      setMessage({ severity: "error", text: errorMessage });
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const selectedAttribute = attributes.find((item) => item.attributeId === attributeId);
  const selectedValueIdsForAttribute = attributeValueIds;
  const persistedAttributeValueIds = attributeId ? (selectedAttributes[attributeId] || []) : [];
  const hasAttributeSelectionChange = attributeId && (
    selectedValueIdsForAttribute.length !== persistedAttributeValueIds.length ||
    selectedValueIdsForAttribute.some((valueId) => !persistedAttributeValueIds.includes(valueId))
  );

  const selectedAttributeChips = useMemo(() =>
    Object.entries(selectedAttributes).map(([id, valueIds]) => {
      const config = attributes.find((item) => item.attributeId === id);
      const values = valueIds.map((valueId) => {
        const value = config?.values.find((item) => item.id === valueId);
        return { id: valueId, name: value?.name || valueId };
      });
      return {
        id,
        attributeName: config?.attributeName || id,
        values,
      };
    }).filter((item) => item.values.length > 0),
  [selectedAttributes, attributes]);

  const activeAttributeChip = selectedAttributeChips.find((item) => item.id === attributePopover.attributeId) || null;

  useEffect(() => {
    if (attributePopover.attributeId && !selectedAttributes[attributePopover.attributeId]) {
      setAttributePopover({ anchorEl: null, attributeId: "" });
    }
  }, [selectedAttributes, attributePopover.attributeId]);

  // A content signature (rather than raw array/object references) so incidental re-renders
  // that produce new-but-equal selections don't spuriously close an open Preview.
  const formSignature = JSON.stringify({
    inviteeEmail,
    selectedRoleId,
    customize,
    selectedRegions: [...selectedRegions].sort(),
    selectedCountries: [...selectedCountries].sort(),
    selectedBottlers: [...selectedBottlers].sort(),
    selectedAttributes,
  });

  // Only close Preview if it is actually open; avoids running on every keystroke before it's shown.
  useEffect(() => {
    setShowPreview((current) => (current ? false : current));
  }, [formSignature]);

  const previewSummary = useMemo(() => {
    const regionNames = selectedRegions;
    const countryNames = countryOptions
      .filter((c) => selectedCountries.includes(c.key))
      .map((c) => c.label);
    const bottlerGroups = Array.from(
      bottlerOptions
        .filter((b) => selectedBottlers.includes(b.key))
        .reduce((groupMap, item) => {
          const groupKey = item.groupKey || item.countryCode;
          const existingGroup = groupMap.get(groupKey);
          if (existingGroup) {
            existingGroup.bottlerNames.push(item.displayLabel || item.label);
            return groupMap;
          }
          groupMap.set(groupKey, {
            key: groupKey,
            countryName: item.groupLabel || item.countryName,
            bottlerNames: [item.displayLabel || item.label],
          });
          return groupMap;
        }, new Map())
        .values()
    );
    const bottlerCount = bottlerGroups.reduce((total, group) => total + group.bottlerNames.length, 0);
    const attrGroups = Object.entries(selectedAttributes).map(([id, values]) => {
      const config = attributes.find((item) => item.attributeId === id);
      return {
        attributeName: config?.attributeName || id,
        values: values.map((valueId) => {
          const value = config?.values.find((item) => item.id === valueId);
          return value?.name || valueId;
        }),
      };
    }).filter((group) => group.values.length > 0);
    const attrCount = attrGroups.reduce((total, group) => total + group.values.length, 0);
    return {
      applicationLabel: applicationType === APPLICATION_TYPE ? APPLICATION_LABEL : applicationType,
      inviteeEmail: inviteeEmail.trim() || "-",
      roleName: selectedRole?.roleName || "-",
      customize,
      regionNames,
      countryNames,
      bottlerGroups,
      bottlerCount,
      attrGroups,
      attrCount,
    };
  }, [applicationType, inviteeEmail, selectedRole, selectedRegions, countryOptions, selectedCountries, bottlerOptions, selectedBottlers, selectedAttributes, attributes, customize]);

  if (loading || (!initialFormState && !configurationError)) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-label="Loading bottler details"
        sx={{
          ...styles.root,
          minHeight: { xs: 280, md: 420 },
          alignItems: "center",
          justifyContent: "center",
          gap: 1.25,
        }}
      >
        <CircularProgress size={32} />
        <Typography sx={{ color: "#64748B", fontSize: "0.85rem", fontWeight: 600 }}>
          Loading bottler details...
        </Typography>
      </Box>
    );
  }

  if (configurationError) {
    return (
      <Box sx={styles.root}>
        <Box sx={styles.mainColumn}>
          <Paper elevation={0} sx={styles.sectionCard}>
            <Alert severity="error">{configurationError}</Alert>
            {onCancel && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="outlined" sx={styles.secondaryButton} onClick={onCancel}>
                  Back to Manage Bottler
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.mainColumn}>
        <Paper elevation={0} sx={styles.sectionCard}>
          <SectionHeader index="01" title="Request Details" subtitle="Review the bottler email address." />
          <Box sx={styles.requestGrid}>
            <Box sx={styles.requestLeftGrid}>
              <Box sx={styles.emailField}>
                <FieldLabel text="Email" required />
                <Box sx={styles.emailInputRow}>
                  <Box sx={styles.emailInputContainer}>
                    <TextField
                      id="edit-bottler-email"
                      required
                      size="small"
                      fullWidth
                      type="email"
                      value={inviteeEmail}
                      placeholder="Enter email address"
                      disabled
                      error={showDomainError}
                      inputProps={{ "aria-label": "Email", "aria-required": true }}
                      sx={[
                        styles.input,
                        styles.emailInput,
                        emailDomainStatus === "checking" && styles.emailInputChecking,
                        emailDomainStatus === "verified" && styles.emailInputVerified,
                      ]}
                    />
                  </Box>
                </Box>
                {emailDomainStatus !== "idle" && (
                  <Box
                    component="div"
                    role="status"
                    aria-live="polite"
                    aria-label={emailDomainStatusLabel}
                    sx={styles.emailStatusRow}
                  >
                    <Box
                      component="span"
                      sx={[
                        styles.emailStatusBadge,
                        emailDomainStatus === "checking" && styles.emailStatusChecking,
                        emailDomainStatus === "verified" && styles.emailStatusVerified,
                        emailDomainStatus === "rejected" && styles.emailStatusRejected,
                      ]}
                    >
                      {emailDomainStatus === "checking" && (
                        <CircularProgress
                          aria-hidden="true"
                          size={13}
                          thickness={5}
                          sx={styles.emailStatusSpinner}
                        />
                      )}
                      {emailDomainStatus === "verified" && (
                        <CheckCircleOutlineIcon aria-hidden="true" sx={styles.emailStatusIcon} />
                      )}
                      {emailDomainStatus === "rejected" && (
                        <CloseIcon aria-hidden="true" sx={styles.emailStatusIcon} />
                      )}
                    </Box>
                    <Typography component="span" sx={[
                      styles.emailStatusText,
                      emailDomainStatus === "checking" && styles.emailStatusTextChecking,
                      emailDomainStatus === "verified" && styles.emailStatusTextVerified,
                      emailDomainStatus === "rejected" && styles.emailStatusTextRejected,
                    ]}
                    >
                      {emailDomainHelperText}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={styles.sectionCard}>
          <SectionHeader index="02" title="Role Assignment" subtitle="Role cannot be changed once a bottler is invited." />
          <Box sx={styles.roleContent}>
            <Box sx={styles.roleLeftGrid}>
              <Box sx={styles.roleSelect}>
                <Box sx={styles.roleFieldHeader}>
                  <FieldLabel text="Role" required />
                </Box>
                <FormControl size="small" fullWidth required disabled>
                  <Select
                    required
                    disabled
                    value={selectedRoleId}
                    onChange={handleRoleChange}
                    inputProps={{ "aria-label": "Role" }}
                    SelectDisplayProps={{ "aria-required": "true" }}
                    sx={styles.select}
                  >
                    {roles.map((role) => <MenuItem key={role.roleId} value={role.roleId}>{role.roleName}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box component="label" sx={styles.rolePermissionStrip}>
              <Typography component="span" sx={styles.rolePermissionLabel}>Customize</Typography>
              <Switch
                size="small"
                checked={customize}
                onChange={handleCustomizeChange}
                inputProps={{ "aria-label": "Customize permissions" }}
                sx={styles.switch}
              />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={styles.sectionCard}>
          <SectionHeader index="03" title="Access Scope" subtitle="Region is fixed. Update countries and bottlers as needed." />
          <Box sx={styles.scopeGrid}>
            <Box sx={styles.scopeLeftGrid}>
              <SelectionPanel title="Region" required count={selectedRegions.length} items={lockedRegionItems} selected={selectedRegions} onToggle={() => {}} selectionControl="radio" />
              <SelectionPanel
                title="Country"
                required
                count={selectedCountries.length}
                items={countryOptions}
                selected={selectedCountries}
                onToggle={(key) => toggle(setSelectedCountries, key)}
                emptyText="Select a region first"
                emptyDescription="Choose a region to view available countries."
                grouped
                showGroupHeader={false}
                allowSelectAll
                selectAllLabel="Select all countries"
              />
            </Box>
            <SelectionPanel
              title="Bottler"
              required
              count={selectedBottlers.length}
              items={bottlerOptions}
              selected={selectedBottlers}
              onToggle={(key) => toggle(setSelectedBottlers, key)}
              emptyText="Select a country first"
              emptyDescription="Choose a country to view available bottlers."
              grouped
              showGroupHeader
              allowSelectAll
              selectAllLabel="Select all bottlers"
            />
          </Box>
        </Paper>

        {customize && (
        <Paper elevation={0} sx={styles.sectionCard}>
          <SectionHeader index="04" title="Additional Attributes" subtitle="Add attributes to refine access." />
          <Box sx={styles.attributeWorkspace}>
            <Box component="section" aria-labelledby="edit-attribute-editor-title" sx={styles.attributeEditorPanel}>
              <Box sx={styles.attributePanelHeader}>
                <Box>
                  <Typography id="edit-attribute-editor-title" sx={styles.attributePanelTitle}>Configure attribute</Typography>
                  <Typography sx={styles.attributePanelSubtitle}>Choose an attribute and its values.</Typography>
                </Box>
              </Box>
              <Box sx={styles.attributeFields}>
                <Box>
                  <FieldLabel text="Data Attribute" />
                  <FormControl size="small" fullWidth>
                    <Select
                      value={attributeId}
                      displayEmpty
                      inputProps={{ "aria-label": "Data Attribute" }}
                      onChange={(event) => {
                        const nextAttributeId = event.target.value;
                        setAttributeId(nextAttributeId);
                        setAttributeValueIds(selectedAttributes[nextAttributeId] || []);
                      }}
                      sx={styles.select}
                    >
                      <MenuItem value="" disabled>Select attribute</MenuItem>
                      {attributes.map((item) => <MenuItem key={item.attributeId} value={item.attributeId}>{item.attributeName}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FieldLabel text="Value" />
                  <FormControl size="small" fullWidth>
                    <SearchableMultiSelect
                      value={selectedValueIdsForAttribute}
                      options={selectedAttribute?.values || []}
                      disabled={!attributeId}
                      ariaLabel="Attribute values"
                      searchContext={selectedAttribute?.attributeName || "Attribute"}
                      placeholder="Select values"
                      onChange={(event) => {
                        const next = event.target.value;
                        setAttributeValueIds(typeof next === "string" ? next.split(",") : next);
                      }}
                    />
                  </FormControl>
                </Box>
                <Button variant="outlined" sx={styles.addButton} disabled={!attributeId || !hasAttributeSelectionChange} onClick={addAttribute}>+ Add attribute</Button>
              </Box>
            </Box>

            <Box component="section" aria-labelledby="edit-selected-attributes-title" sx={styles.selectedAttributesPanel}>
              <Box sx={styles.attributePanelHeader}>
                <Box>
                  <Typography id="edit-selected-attributes-title" sx={styles.attributePanelTitle}>Selected attributes</Typography>
                  <Typography sx={styles.attributePanelSubtitle}>Select a chip to view or remove its values.</Typography>
                </Box>
                <Box component="span" sx={styles.selectedAttributesCount}>{selectedAttributeChips.length} selected</Box>
              </Box>
              <Box sx={styles.chipArea} aria-live="polite">
                {selectedAttributeChips.length ? selectedAttributeChips.map((item) => (
                  <Box
                    component="button"
                    type="button"
                    key={item.id}
                    sx={styles.attributeChipWrapper}
                    aria-label={`${item.attributeName}, ${item.values.length} selected ${item.values.length === 1 ? "value" : "values"}`}
                    aria-haspopup="dialog"
                    aria-expanded={attributePopover.attributeId === item.id && Boolean(attributePopover.anchorEl)}
                    aria-controls={attributePopover.attributeId === item.id && Boolean(attributePopover.anchorEl) ? "edit-attribute-values-popover" : undefined}
                    onClick={(event) => openAttributePopover(event, item.id)}
                    onMouseEnter={(event) => openAttributePopover(event, item.id)}
                    onMouseLeave={scheduleAttributePopoverClose}
                  >
                    <Chip
                      size="small"
                      label={(
                        <Box component="span" sx={styles.attributeChipLabel}>
                          <Box component="span" sx={styles.attributeChipName}>{item.attributeName}</Box>
                          <Box component="span" sx={styles.attributeChipCount}>{item.values.length}</Box>
                          <KeyboardArrowDownIcon
                            sx={{
                              ...styles.attributeChipChevron,
                              ...(attributePopover.attributeId === item.id && Boolean(attributePopover.anchorEl) ? styles.attributeChipChevronOpen : {}),
                            }}
                          />
                        </Box>
                      )}
                      sx={{
                        ...styles.attributeChip,
                        ...(attributePopover.attributeId === item.id && Boolean(attributePopover.anchorEl) ? styles.attributeChipOpen : {}),
                      }}
                    />
                  </Box>
                )) : (
                  <Box sx={styles.selectedAttributesEmpty}>
                    <Typography sx={styles.selectedAttributesEmptyTitle}>No attributes selected</Typography>
                    <Typography sx={styles.selectedAttributesEmptyText}>Use the controls on the left to add attributes.</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Popover
            open={Boolean(attributePopover.anchorEl) && Boolean(activeAttributeChip)}
            anchorEl={attributePopover.anchorEl}
            onClose={closeAttributePopover}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            marginThreshold={8}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            sx={styles.attributePopoverContainer}
            slotProps={{
              paper: {
                id: "edit-attribute-values-popover",
                role: "dialog",
                "aria-labelledby": "edit-attribute-popover-title",
                sx: styles.attributePopoverPaper,
                onMouseEnter: clearAttributePopoverCloseTimer,
                onMouseLeave: scheduleAttributePopoverClose,
                onFocusCapture: clearAttributePopoverCloseTimer,
              },
            }}
          >
            <Box sx={styles.attributePopoverContent}>
              <Box sx={styles.attributePopoverHeader}>
                <Box sx={styles.attributePopoverHeading}>
                  <Typography component="h4" id="edit-attribute-popover-title" sx={styles.attributePopoverTitle}>{activeAttributeChip?.attributeName}</Typography>
                  <Typography sx={styles.attributePopoverSubtitle}>Selected values</Typography>
                </Box>
                <Box sx={styles.attributePopoverActions}>
                  <Box
                    component="span"
                    aria-label={`${activeAttributeChip?.values.length || 0} selected ${(activeAttributeChip?.values.length || 0) === 1 ? "value" : "values"}`}
                    sx={styles.attributePopoverCount}
                  >
                    {activeAttributeChip?.values.length || 0}
                  </Box>
                  <IconButton size="small" aria-label="Close selected values" onClick={closeAttributePopover} sx={styles.attributePopoverClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={styles.attributePopoverList}>
                {(activeAttributeChip?.values || []).map((value) => (
                  <Chip
                    key={`${activeAttributeChip.id}-${value.id}`}
                    size="small"
                    label={value.name}
                    onDelete={() => removeAttribute(activeAttributeChip.id, value.id)}
                    deleteIcon={<CloseIcon fontSize="small" />}
                    sx={styles.attributePopoverValueChip}
                  />
                ))}
              </Box>
              <Typography sx={styles.attributePopoverHint}>Use a value&apos;s close icon to remove it.</Typography>
            </Box>
          </Popover>
        </Paper>
        )}
        <Box sx={styles.actionRow}>
          <Button
            type="button"
            variant="outlined"
            sx={styles.secondaryButton}
            disabled={submitting || loading || !isFormComplete}
            aria-expanded={showPreview}
            aria-controls="sop-edit-preview"
            onClick={() => setShowPreview((current) => !current)}
          >
            {showPreview ? "Hide preview" : "Preview"}
          </Button>
          <Button variant="outlined" sx={styles.secondaryButton} disabled={submitting} onClick={onCancel}>Cancel</Button>
          <Button variant="outlined" sx={styles.secondaryButton} disabled={submitting} onClick={handleReset}>Reset</Button>
          <Button variant="contained" sx={styles.primaryButton} disabled={submitting || loading || !isFormComplete || !hasChanges} onClick={() => submit()}>{submitting ? <CircularProgress size={18} sx={styles.buttonSpinner} /> : 'Update'}</Button>
        </Box>

        {showPreview && (
          <Paper component="section" aria-labelledby="sop-edit-preview-title" id="sop-edit-preview" elevation={0} sx={styles.previewCard}>
            <Box sx={styles.previewHeader}>
              <Box sx={styles.previewHeaderHeading}>
                <Box sx={styles.previewHeaderAccent} />
                <Typography component="h2" id="sop-edit-preview-title" sx={styles.previewTitle}>Update Preview</Typography>
              </Box>
              <Box component="span" sx={styles.previewLiveBadge}><Box component="span" sx={styles.previewLiveDot} />Live preview</Box>
            </Box>
            <Box component="dl" sx={styles.previewSummaryGrid}>
              {[
                ["Application", previewSummary.applicationLabel],
                ["Role", previewSummary.roleName],
                ["Email", previewSummary.inviteeEmail],
                ["Customize", previewSummary.customize ? "Yes" : "No"],
              ].map(([label, value]) => (
                <Box component="div" key={label} sx={styles.previewSummaryItem}>
                  <Typography component="dt" sx={styles.previewSummaryLabel}>{label}</Typography>
                  <Typography component="dd" sx={styles.previewSummaryValue}>{value}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={styles.previewScopeTitleRow}>
              <Typography component="h3" sx={{ ...styles.previewSectionTitle, ...styles.previewScopeTitle }}>Access Scope</Typography>
              <Box component="dl" aria-label="Selected region" sx={styles.previewInlineRegion}>
                <Typography component="dt" sx={styles.previewInlineRegionLabel}>Region</Typography>
                <Box component="dd" sx={styles.previewInlineRegionValue}>
                  {previewSummary.regionNames.length
                    ? previewSummary.regionNames.map((name) => <Chip key={name} size="small" label={name} sx={{ ...styles.previewChip, ...styles.previewRegionChip }} />)
                    : <Typography component="span" sx={styles.previewEmptyValue}>None</Typography>}
                </Box>
              </Box>
            </Box>
            <Box sx={styles.previewScopeGrid}>
              <Box component="section" sx={styles.previewScopeGroup}>
                <Box sx={styles.previewScopeHeader}>
                  <Typography component="h4" sx={styles.previewScopeLabel}>Countries</Typography>
                  <Box component="span" sx={styles.previewScopeCount}>{previewSummary.countryNames.length} selected</Box>
                </Box>
                <Box sx={styles.previewChipRow}>
                  {previewSummary.countryNames.length
                    ? previewSummary.countryNames.map((name) => <Chip key={name} size="small" label={name} sx={styles.previewChip} />)
                    : <Typography sx={styles.previewEmptyValue}>None</Typography>}
                </Box>
              </Box>
              <Box component="section" sx={styles.previewScopeGroup}>
                <Box sx={styles.previewScopeHeader}>
                  <Typography component="h4" sx={styles.previewScopeLabel}>Bottlers</Typography>
                  <Box component="span" sx={styles.previewScopeCount}>{previewSummary.bottlerCount} selected</Box>
                </Box>
                {previewSummary.bottlerGroups.length ? previewSummary.bottlerGroups.map((group) => (
                  <Box key={group.key} sx={styles.previewBottlerCountryGroup}>
                    <Typography component="h5" sx={styles.previewBottlerCountryLabel}>{group.countryName}</Typography>
                    <Box sx={styles.previewChipRow}>
                      {group.bottlerNames.map((name) => <Chip key={`${group.key}-${name}`} size="small" label={name} sx={styles.previewChip} />)}
                    </Box>
                  </Box>
                )) : <Typography sx={styles.previewEmptyValue}>None</Typography>}
              </Box>
            </Box>
            {previewSummary.attrGroups.length > 0 && (
              <>
                <Typography component="h3" sx={styles.previewSectionTitle}>Additional Attributes</Typography>
                <Box sx={styles.previewAttributeGrid}>
                  {previewSummary.attrGroups.map((group) => (
                    <Box component="section" key={group.attributeName} sx={styles.previewAttributeGroup}>
                      <Box sx={styles.previewScopeHeader}>
                        <Typography component="h4" sx={styles.previewAttributeGroupLabel}>{group.attributeName}</Typography>
                        <Box component="span" sx={styles.previewScopeCount}>{group.values.length} selected</Box>
                      </Box>
                      <Box sx={styles.previewChipRow}>
                        {group.values.map((value) => <Chip key={`${group.attributeName}-${value}`} size="small" label={value} sx={styles.previewChip} />)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        )}

        
      </Box>
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={6000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={message?.severity || "info"} onClose={() => setMessage(null)}>
          {message?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const FieldLabel = ({ text, required = false }) => <Typography sx={styles.fieldLabel}>{text}{required && <Box component="span" sx={styles.required}> *</Box>}</Typography>;
const SectionHeader = ({ index, title, subtitle }) => <Box sx={styles.sectionHeaderRow}><Box sx={styles.sectionHeading}><Box><Typography component="h2" sx={styles.sectionTitle}>{title}</Typography><Typography sx={styles.sectionSubtitle}>{subtitle}</Typography></Box></Box></Box>;

const SearchableMultiSelect = ({ value = [], options = [], disabled, ariaLabel, searchContext, placeholder, onChange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const filteredOptions = normalizedSearchTerm
    ? options.filter((option) => String(option.name || "").toLocaleLowerCase().includes(normalizedSearchTerm))
    : options;
  const selectedOptions = options.filter((option) => value.includes(option.id));
  const optionListLabel = `${searchContext || ariaLabel} values`;

  const resetSearch = () => setSearchTerm("");

  return (
    <Select
      multiple
      value={value}
      displayEmpty
      disabled={disabled}
      inputProps={{ "aria-label": ariaLabel }}
      onChange={onChange}
      onOpen={resetSearch}
      onClose={resetSearch}
      renderValue={() => {
        if (!selectedOptions.length) return <Box component="span" sx={styles.multiSelectPlaceholder}>{placeholder}</Box>;
        return (
          <Box component="span" sx={styles.multiSelectValue}>
            <Box component="span" sx={styles.multiSelectValueName}>{selectedOptions[0].name}</Box>
            {selectedOptions.length > 1 && <Box component="span" sx={styles.multiSelectValueCount}>+{selectedOptions.length - 1}</Box>}
          </Box>
        );
      }}
      MenuProps={{
        autoFocus: false,
        PaperProps: { sx: styles.multiSelectMenuPaper },
        MenuListProps: {
          autoFocusItem: false,
          "aria-label": optionListLabel,
          "aria-multiselectable": "true",
          subheader: (
            <ListSubheader sx={styles.multiSelectSearchHeader}>
              <Box sx={styles.multiSelectSearchMeta}>
                <Typography sx={styles.multiSelectSearchTitle}>Available values</Typography>
                <Typography aria-live="polite" sx={styles.multiSelectSearchCount}>{value.length} selected</Typography>
              </Box>
              <TextField
                autoFocus
                type="search"
                size="small"
                fullWidth
                value={searchTerm}
                placeholder="Search values..."
                inputProps={{ "aria-label": `Search ${optionListLabel}` }}
                onChange={(event) => setSearchTerm(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (!["ArrowDown", "ArrowUp", "Escape", "Tab"].includes(event.key)) event.stopPropagation();
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={styles.multiSelectSearchIcon} /></InputAdornment>,
                }}
                sx={styles.multiSelectSearchInput}
              />
              {!filteredOptions.length && (
                <Typography component="div" role="status" aria-live="polite" sx={styles.multiSelectNoResults}>
                  {normalizedSearchTerm ? `No values match "${searchTerm.trim()}"` : "No values available"}
                </Typography>
              )}
            </ListSubheader>
          ),
          sx: styles.multiSelectMenuList,
        },
      }}
      sx={styles.select}
    >
      {filteredOptions.map((option) => (
        <MenuItem key={option.id} value={option.id} sx={styles.multiSelectOption}>
          <Checkbox
            size="small"
            checked={value.includes(option.id)}
            tabIndex={-1}
            disableRipple
            inputProps={{ "aria-hidden": true, tabIndex: -1 }}
            sx={styles.checkbox}
          />
          <Typography sx={styles.multiSelectOptionLabel}>{option.name}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
};

const SelectionOption = ({ item, selected, onToggle, showParentLabel = true, selectionControl = "checkbox" }) => {
  const isSelected = selected.includes(item.key);
  const isDisabled = Boolean(item.disabled);
  const SelectionControl = selectionControl === "radio" ? Radio : Checkbox;

  return (
    <Box
      component="label"
      sx={{ ...styles.selectionItem, ...(isDisabled ? styles.selectionItemDisabled : {}) }}
    >
      <SelectionControl
        size="small"
        checked={isSelected}
        disabled={isDisabled}
        onChange={() => onToggle(item.key)}
        inputProps={{ "aria-label": item.displayLabel || item.label }}
        sx={styles.checkbox}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={styles.selectionItemText}>{item.displayLabel || item.label}</Typography>
        {showParentLabel && item.parentLabel && <Box component="span" sx={styles.selectionItemParentTag}>{item.parentLabel}</Box>}
        {isDisabled && <Box component="span" sx={styles.selectionItemNoDataTag}>{item.noDataText || "No data available"}</Box>}
      </Box>
    </Box>
  );
};

const SelectionPanel = ({
  title,
  required = false,
  count,
  items,
  selected,
  onToggle,
  emptyText = "No options available",
  emptyDescription = "Options will appear here when available.",
  grouped = false,
  showGroupHeader = true,
  showParentLabel = true,
  allowSelectAll = false,
  selectAllLabel = "Select all",
  selectionControl = "checkbox",
}) => {
  const isCountryOrBottlerPanel = ["country", "bottler"].includes(String(title).toLowerCase());
  const panelId = `edit-selection-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const groups = grouped ? Array.from(items.reduce((groupMap, item) => {
    const groupKey = item.groupKey || item.parentLabel || "other";
    const existingGroup = groupMap.get(groupKey);
    if (existingGroup) {
      existingGroup.items.push(item);
      return groupMap;
    }
    groupMap.set(groupKey, {
      key: groupKey,
      label: item.groupLabel || item.parentLabel || "Other",
      context: item.groupContext || "",
      items: [item],
    });
    return groupMap;
  }, new Map()).values()) : [];

  const selectableItems = items.filter((item) => !item.disabled);
  const selectableKeys = selectableItems.map((item) => item.key);
  const selectedSelectableCount = selectableKeys.filter((key) => selected.includes(key)).length;
  const hasSelectableItems = selectableKeys.length > 0;
  const isAllSelected = hasSelectableItems && selectedSelectableCount === selectableKeys.length;
  const isPartiallySelected = selectedSelectableCount > 0 && !isAllSelected;
  const headerSelectionText = isCountryOrBottlerPanel
    ? `${count}/${items.length} selected`
    : `${count} selected`;

  const handleSelectAllToggle = () => {
    if (!hasSelectableItems) return;
    selectableKeys.forEach((key) => {
      const keySelected = selected.includes(key);
      if (isAllSelected ? keySelected : !keySelected) {
        onToggle(key);
      }
    });
  };

  return (
    <Box component="section" aria-labelledby={`${panelId}-title`} aria-required={required || undefined} sx={styles.selectionPanel}>
      <Box sx={styles.selectionHeader}>
        <Typography component="h3" id={`${panelId}-title`} sx={styles.selectionTitle}>
          {title}
          {required && <Box component="span" aria-hidden="true" sx={styles.required}> *</Box>}
        </Typography>
        <Box component="span" aria-live="polite" sx={styles.selectedAttributesCount}>{headerSelectionText}</Box>
      </Box>
      <Box sx={{ ...styles.selectionList, ...(grouped ? styles.selectionGroupList : {}) }}>
        {allowSelectAll && items.length > 0 && (
          <Box sx={{ borderBottom: "1px solid #E2E8F0", mb: 0.65, pb: 0.5 }}>
            <Box component="label" sx={styles.selectionItem}>
              <Checkbox
                size="small"
                checked={isAllSelected}
                indeterminate={isPartiallySelected}
                disabled={!hasSelectableItems}
                onChange={handleSelectAllToggle}
                inputProps={{ "aria-label": selectAllLabel }}
                sx={styles.checkbox}
              />
              <Typography sx={{ ...styles.selectionItemText, fontWeight: 700 }}>{selectAllLabel}</Typography>
            </Box>
          </Box>
        )}
        {items.length ? (grouped ? groups.map((group, groupIndex) => {
          const groupTitleId = `${panelId}-group-${groupIndex}`;
          const groupContextId = `${groupTitleId}-context`;
          const groupSelectedCount = group.items.filter((item) => selected.includes(item.key)).length;
          const showGroupContext = Boolean(group.context) && groups.some((candidate) => candidate.key !== group.key && candidate.label === group.label);
          return (
            <Box
              key={group.key}
              role="group"
              aria-labelledby={showGroupHeader ? `${groupTitleId}${showGroupContext ? ` ${groupContextId}` : ""}` : undefined}
              sx={
                showGroupHeader
                  ? styles.selectionGroup
                  : { ...styles.selectionGroup, "& + &": { mt: 0, pt: 0, borderTop: "none" } }
              }
            >
              {showGroupHeader && (
                <Box sx={styles.selectionGroupHeader}>
                  <Box sx={styles.selectionGroupHeading}>
                    <Box sx={styles.selectionGroupAccent} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="h4" id={groupTitleId} sx={styles.selectionGroupTitle}>{group.label}</Typography>
                      {showGroupContext && <Typography id={groupContextId} sx={styles.selectionGroupContext}>{group.context}</Typography>}
                    </Box>
                  </Box>
                  <Typography aria-label={`${groupSelectedCount} of ${group.items.length} selected`} sx={styles.selectionGroupCount}>{groupSelectedCount}/{group.items.length}</Typography>
                </Box>
              )}
              <Box sx={styles.selectionGroupItems}>
                {group.items.map((item) => <SelectionOption key={item.key} item={item} selected={selected} onToggle={onToggle} showParentLabel={false} selectionControl={selectionControl} />)}
              </Box>
            </Box>
          );
        }) : items.map((item) => <SelectionOption key={item.key} item={item} selected={selected} onToggle={onToggle} showParentLabel={showParentLabel} selectionControl={selectionControl} />)) : (
          <Box role="status" aria-live="polite" sx={styles.selectionEmptyState}>
            <Box sx={styles.selectionEmptyIconTile}>
              <InfoRoundedIcon aria-hidden="true" sx={styles.selectionEmptyIcon} />
            </Box>
            <Typography sx={styles.selectionEmptyTitle}>{emptyText}</Typography>
            <Typography sx={styles.selectionEmptyDescription}>{emptyDescription}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EditBottlerForm;
